
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl, generateArticleSchema } from '@/utils/seoUtils';
import HeroSection from '@/components/social-media/HeroSection';
import WhyKIVideos from '@/components/social-media/WhyKIVideos';
import ExampleGallery from '@/components/social-media/ExampleGallery';
import ProcessSteps from '@/components/social-media/ProcessSteps';
import FAQSection from '@/components/social-media/FAQSection';
import CtaSection from '@/components/social-media/CtaSection';
import FloatingCTA from '@/components/social-media/FloatingCTA';
import PageTransition from '@/components/PageTransition';

const SocialMediaAdsPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Get language-specific SEO keywords based on current language
    const getLanguageSpecificKeywords = () => {
      switch (currentLanguage) {
        case 'de':
          return "Social Media Ads, KI Video Ads, KI-optimierte Videos, Facebook Werbung, Instagram Videos, TikTok Videos, AI Video Marketing, LinkedIn Anzeigen, Social Media Marketing Schweiz";
        case 'en':
          return "Social Media Ads, AI Video Ads, AI-optimized Videos, Facebook Advertising, Instagram Videos, TikTok Videos, AI Video Marketing, LinkedIn Ads, Social Media Marketing Switzerland";
        case 'fr':
          return "Publicités pour Médias Sociaux, Publicités Vidéo IA, Vidéos optimisées par l'IA, Publicité Facebook, Vidéos Instagram, Vidéos TikTok, Marketing Vidéo IA, Annonces LinkedIn, Marketing des Médias Sociaux Suisse";
        case 'it':
          return "Pubblicità per Social Media, Pubblicità Video IA, Video ottimizzati con IA, Pubblicità Facebook, Video Instagram, Video TikTok, Marketing Video IA, Annunci LinkedIn, Marketing Social Media Svizzera";
        default:
          return "Social Media Ads, KI Video Ads, Facebook Ads, Instagram Videos, TikTok Videos, AI Video Marketing, LinkedIn Ads";
      }
    };

    // Get language-specific meta description based on current language
    const getLanguageSpecificDescription = () => {
      if (t.socialMediaAdsDesc) return t.socialMediaAdsDesc;
      
      switch (currentLanguage) {
        case 'de':
          return "Hochperformante KI-optimierte Videos für Ihre Social Media Kampagnen auf Instagram, Facebook, TikTok & Co. Steigern Sie Ihr Engagement um bis zu 300% mit Future Level.";
        case 'en':
          return "High-performance AI-optimized videos for your social media campaigns on Instagram, Facebook, TikTok & more. Increase your engagement by up to 300% with Future Level.";
        case 'fr':
          return "Vidéos haute performance optimisées par l'IA pour vos campagnes sur les médias sociaux Instagram, Facebook, TikTok et plus. Augmentez votre engagement jusqu'à 300% avec Future Level.";
        case 'it':
          return "Video ad alte prestazioni ottimizzati con IA per le tue campagne sui social media Instagram, Facebook, TikTok e altro. Aumenta il tuo engagement fino al 300% con Future Level.";
        default:
          return "KI-optimierte Videos für Social Media Ads in der Schweiz";
      }
    };
    
    // Update SEO meta tags with enhanced keywords and descriptions
    updateMetaTags({
      title: `${t.socialMediaAds || "Social Media Ads"} | Future Level`,
      description: getLanguageSpecificDescription(),
      keywords: getLanguageSpecificKeywords(),
      canonicalUrl: getCanonicalUrl('services/social-media-ads', currentLanguage),
      language: currentLanguage,
      ogType: "article"
    });
    
    // Add enhanced structured data for SEO
    generateArticleSchema(
      `${t.socialMediaAds || "Social Media Ads"} | Future Level`,
      getLanguageSpecificDescription(),
      "https://future-level.ch/opengraph-image-p98pqg.png",
      new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
      getCanonicalUrl('services/social-media-ads', currentLanguage),
      currentLanguage,
      getLanguageSpecificKeywords()
    );
    
    // Add service-specific Schema.org markup with enhanced descriptions for better search visibility
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${t.socialMediaAds || "Social Media Ads"}`,
      "description": getLanguageSpecificDescription(),
      "provider": {
        "@type": "Organization",
        "name": "Future Level",
        "url": "https://future-level.ch"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Switzerland"
      },
      "serviceType": "Social Media Video Marketing",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock"
      }
    };
    
    // Add service schema to the page
    let scriptTag = document.getElementById('service-schema') as HTMLScriptElement;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'service-schema';
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(serviceSchema);
    
    // Add additional FAQ Schema for better SEO visibility
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": currentLanguage === 'de' ? "Was sind KI Social Media Ads?" :
                 currentLanguage === 'en' ? "What are AI Social Media Ads?" :
                 currentLanguage === 'fr' ? "Que sont les publicités pour médias sociaux IA?" :
                 "Cosa sono le pubblicità per social media con IA?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": currentLanguage === 'de' ? "KI Social Media Ads sind automatisierte Videos, die mit Hilfe von künstlicher Intelligenz erstellt werden und für Social Media Plattformen optimiert sind." :
                   currentLanguage === 'en' ? "AI Social Media Ads are automated videos created with artificial intelligence that are optimized for social media platforms." :
                   currentLanguage === 'fr' ? "Les publicités pour médias sociaux IA sont des vidéos automatisées créées à l'aide de l'intelligence artificielle et optimisées pour les plateformes de médias sociaux." :
                   "Le pubblicità per social media con IA sono video automatizzati creati con l'intelligenza artificiale e ottimizzati per le piattaforme di social media."
          }
        },
        {
          "@type": "Question",
          "name": currentLanguage === 'de' ? "Wie können KI Videos mein Engagement steigern?" :
                 currentLanguage === 'en' ? "How can AI videos increase my engagement?" :
                 currentLanguage === 'fr' ? "Comment les vidéos IA peuvent-elles augmenter mon engagement?" :
                 "Come possono i video con IA aumentare il mio engagement?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": currentLanguage === 'de' ? "KI-optimierte Videos erhöhen das Engagement durch personalisierte Inhalte, die auf Ihre Zielgruppe zugeschnitten sind und aktuelle Trends nutzen." :
                   currentLanguage === 'en' ? "AI-optimized videos increase engagement through personalized content tailored to your target audience and leveraging current trends." :
                   currentLanguage === 'fr' ? "Les vidéos optimisées par l'IA augmentent l'engagement grâce à un contenu personnalisé adapté à votre public cible et exploitant les tendances actuelles." :
                   "I video ottimizzati con IA aumentano il coinvolgimento attraverso contenuti personalizzati adattati al tuo pubblico di riferimento e sfruttando le tendenze attuali."
          }
        }
      ]
    };
    
    // Add FAQ schema to the page
    let faqScriptTag = document.getElementById('faq-schema') as HTMLScriptElement;
    if (!faqScriptTag) {
      faqScriptTag = document.createElement('script');
      faqScriptTag.id = 'faq-schema';
      faqScriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(faqScriptTag);
    }
    faqScriptTag.textContent = JSON.stringify(faqSchema);
    
    // Set language attribute for current page section
    document.querySelector('main')?.setAttribute('lang', currentLanguage);
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [currentLanguage, t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <PageTransition>
        <main className="flex-grow">
          <HeroSection />
          <WhyKIVideos />
          <ExampleGallery />
          <ProcessSteps />
          <FAQSection />
          <CtaSection />
        </main>
      </PageTransition>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default SocialMediaAdsPage;
